import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Box, Paper, Typography, CircularProgress, Alert, Button, ButtonGroup, Slider, ToggleButton, ToggleButtonGroup, Chip } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, IconButton, Divider, List, ListItem, ListItemText } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ReferenceLine } from 'recharts';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ReactSankeyDiagram from './ReactSankeyDiagram';
import IncomeBridgeWaterfall from './IncomeBridgeWaterfall';
import SecFilings from './SecFilings';
import enhancedFinancialDataService from '../services/EnhancedFinancialDataService';
import { detectSector, buildIncomeStatementSankey, buildCashFlowSankey } from '../utils/SectorAwareSankeyBuilder';
import { getComponentDescription } from '../utils/SankeyComponentDescriptions';
import {
  normalizeRevenueOperatingSeries,
  getRevenueValue,
  getOperatingIncomeValue,
  getNetIncomeValue,
  isIpoChartPeriod,
  getIpoPeriodLabel,
  isPreliminary8kChartPeriod,
  getPreliminary8kLabel,
  retainOnlyLatestPreliminary8k,
} from '../utils/revenueOperatingData';

const FinancialStatements = ({ 
  ticker: initialTicker, 
  exchange, 
  apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? '',
  visualizations = { 
    incomeStatement: 'sankey', 
    cashFlow: 'sankey', 
    balanceSheet: 'pie' 
  }
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [ticker, setTicker] = useState(initialTicker || '');
  const [revenueData, setRevenueData] = useState([]);
  const [incomeStatementData, setIncomeStatementData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [balanceSheetData, setBalanceSheetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [loadingSections, setLoadingSections] = useState({
    income: false,
    cashFlow: false,
    balance: false,
    transcript: false,
  });
  const [currency, setCurrency] = useState('USD');
  const [financialPeriodType, setFinancialPeriodType] = useState('quarterly');
  const [sector, setSector] = useState('generic');
  const [balanceSheetInfoDialogOpen, setBalanceSheetInfoDialogOpen] = useState(false);
  const [activeView, setActiveView] = useState('statements');
  const [transcriptData, setTranscriptData] = useState(null);
  const [transcriptError, setTranscriptError] = useState('');
  const [transcriptNotice, setTranscriptNotice] = useState('');
  const latestTickerRef = useRef(ticker);

  const balanceLabelHeaders = ['Assets', 'Liabilities'];

  // Currency symbols for display
  const currencySymbols = {
    USD: '$',
    JPY: '¥',
    EUR: '€',
    GBP: '£',
    CNY: '¥',
    TWD: 'NT$',
    KRW: '₩',
    INR: '₹',
    BRL: 'R$',
    CAD: 'C$',
    AUD: 'A$',
    CHF: 'Fr',
    RMB: '¥',
  };
  
  const getCurrencySymbol = () => currencySymbols[currency] || currency || '$';

  // Update ticker when initialTicker changes
  useEffect(() => {
    if (initialTicker) {
      setTicker(initialTicker);
    }
  }, [initialTicker]);

  useEffect(() => {
    latestTickerRef.current = ticker;
    // Reset period-specific visuals immediately on ticker changes so stale
    // responses from the previous ticker cannot appear mixed with new charts.
    setIncomeStatementData(null);
    setCashFlowData(null);
    setBalanceSheetData([]);
    setTranscriptData(null);
    setTranscriptError('');
    setTranscriptNotice('');
    setSelectedPeriod(null);
  }, [ticker]);

  // Helper function to format large numbers with 3 significant figures
  const formatValue = (value) => {
    const symbol = getCurrencySymbol();
    if (!value && value !== 0) return `${symbol}0`;
    
    const absValue = Math.abs(value);
    
    // Explicitly round to 3 significant figures before dividing
    let roundedValue;
    let suffix;
    
    if (absValue >= 1000000000) {
      roundedValue = (value / 1000000000).toPrecision(3);
      suffix = 'B';
    } else if (absValue >= 1000000) {
      roundedValue = (value / 1000000).toPrecision(3);
      suffix = 'M';
    } else {
      roundedValue = (value / 1000).toPrecision(3);
      suffix = 'K';
    }
    
    // Remove trailing zeros after decimal point
    roundedValue = parseFloat(roundedValue);
    
    return `${symbol}${roundedValue}${suffix}`;
  };

  // Format currency values for display
  const formatCurrency = (value) => {
    if (!value) return '0';
    const num = parseFloat(value);
    if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    return num.toLocaleString();
  };

  const isLikelyCumulativeAnnualRow = (currentRow, previousRow) => {
    if (!currentRow || !previousRow) return false;

    // Keep residual Q4 points derived from annual 10-K totals.
    if (
      currentRow?.isDerivedQuarter === true
      || String(currentRow?.sourceOrigin || '').toLowerCase() === 'derived-from-10k'
    ) {
      return false;
    }

    const currentDate = new Date(currentRow.period);
    const previousDate = new Date(previousRow.period);
    if (!Number.isFinite(currentDate.getTime()) || !Number.isFinite(previousDate.getTime())) {
      return false;
    }

    const currentMonth = currentDate.getUTCMonth() + 1;
    if (currentMonth !== 12) return false;

    const daySpan = (currentDate.getTime() - previousDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daySpan < 120 || daySpan > 260) return false;

    const currentRevenue = Number(currentRow.Revenue);
    const previousRevenue = Number(previousRow.Revenue);
    if (!Number.isFinite(currentRevenue) || !Number.isFinite(previousRevenue) || previousRevenue <= 0) {
      return false;
    }

    return currentRevenue > previousRevenue * 2.2;
  };

  const buildChartRows = (rows) => {
    const sortedRows = [...(Array.isArray(rows) ? rows : [])].sort(
      (a, b) => new Date(a.period) - new Date(b.period)
    );

    const filtered = sortedRows.length <= 1
      ? sortedRows
      : sortedRows.filter((row, index) => (
        index === 0 || !isLikelyCumulativeAnnualRow(row, sortedRows[index - 1])
      ));

    return retainOnlyLatestPreliminary8k(filtered).map((row) => {
      const isIpoPeriod = isIpoChartPeriod(row);
      const isPreliminary = isPreliminary8kChartPeriod(row) && row.isPreliminary === true;
      return {
        ...row,
        isIpoPeriod,
        ipoLabel: isIpoPeriod ? getIpoPeriodLabel(row.sourceForm, row.ipoLabel) : null,
        isPreliminary,
        preliminaryLabel: isPreliminary
          ? getPreliminary8kLabel(row.preliminaryLabel)
          : null,
      };
    });
  };

  // Calculate max revenue for chart axis
  const getMaxRevenue = (data) => {
    if (!data || data.length === 0) return 0;
    return Math.max(...data.map(item => item.Revenue || 0)) * 1.1; // Add 10% padding
  };

  // Get data range for any data key to properly handle negative values
  const getDataRange = (data, key) => {
    if (!data || data.length === 0) return { min: 0, max: 0 };
    
    const values = data.map(item => Number(item[key]) || 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    // Handle different scenarios for better scaling
    if (min >= 0) {
      // All positive values - start at 0
      return { min: 0, max: max * 1.1 }; // Add 10% padding at top
    } else {
      // Have negative values - need to handle specially
      return { 
        min: min * 1.1, // Add 10% padding below lowest negative value
        max: max > 0 ? max * 1.1 : 0 // Add padding to positive max or use 0 if all negative
      };
    }
  };

  const getCashFromBalanceRow = (row) => {
    if (!row) return 0;
    const cashKeys = [
      'cash',
      'cash_and_cash_equivalents',
      'cashAndCashEquivalents',
      'cash_and_short_term_investments',
      'cashAndShortTermInvestments',
      'cash_and_st_investments',
      'cashAndStInvestments',
      'Cash and Cash Equivalents',
      'Cash & Equivalents',
      'Cash',
    ];

    for (const key of cashKeys) {
      const value = parseFloat(row[key]);
      if (Number.isFinite(value) && value > 0) {
        return value;
      }
    }

    return 0;
  };

  // Fetch balance sheet for a specific period
  const fetchBalanceSheetForPeriod = useCallback(async (period) => {
    const requestedTicker = ticker;
    try {
      setLoadingSections(prev => ({ ...prev, balance: true }));
      
      if (!ticker || !period) {
        console.warn('No ticker or period provided for balance sheet fetch');
        setBalanceSheetData([]);
        return;
      }
      
      const response = await enhancedFinancialDataService.getBalanceSheet(ticker);

      if (requestedTicker !== latestTickerRef.current) {
        return;
      }
      
      if (response && response.length > 0) {
        const exactMatch = response.find(item => item.period === period);
        
        if (exactMatch) {
          setBalanceSheetData([exactMatch]);
          return;
        }
        
        // Find closest period
        const targetDate = new Date(period);
        let closestItem = response[0];
        let smallestDiff = Math.abs(new Date(closestItem.period) - targetDate);
        
        for (const item of response) {
          const itemDate = new Date(item.period);
          const currentDiff = Math.abs(itemDate - targetDate);
          if (currentDiff < smallestDiff) {
            smallestDiff = currentDiff;
            closestItem = item;
          }
        }
        
        setBalanceSheetData([closestItem]);
      } else {
        setBalanceSheetData([]);
      }
    } catch (err) {
      console.error('Error fetching balance sheet for period:', err);
      setBalanceSheetData([]);
    } finally {
      setLoadingSections(prev => ({ ...prev, balance: false }));
    }
  }, [ticker]);

  // Fetch income statement data
  const fetchIncomeStatementData = useCallback(async (period) => {
    const requestedTicker = ticker;
    try {
      setLoadingSections(prev => ({ ...prev, income: true }));
      
      if (!ticker || !period) {
        console.warn('No ticker or period provided for income statement fetch');
        setIncomeStatementData(null);
        return;
      }
      
      // Use the enhanced financial data service
      const response = await enhancedFinancialDataService.getIncomeStatement(ticker, period);

      if (requestedTicker !== latestTickerRef.current) {
        return;
      }
      
      const rawData = Array.isArray(response)
        ? response[0]
        : (response && response.nodes ? null : response);

      if (rawData) {
        setIncomeStatementData(buildIncomeStatementSankey(rawData, sector));
        return;
      }

      if (response && response.nodes && response.links) {
        // Preserve pre-formatted responses from API without estimating missing values.
        setIncomeStatementData(response);
      } else {
        setIncomeStatementData(null);
      }
    } catch (err) {
      console.error(`Error fetching income statement for period ${period}:`, err);
      setIncomeStatementData(null);
    } finally {
      setLoadingSections(prev => ({ ...prev, income: false }));
    }
  }, [ticker, sector]);

  // Fetch cash flow data
  const fetchCashFlowData = useCallback(async (period) => {
    const requestedTicker = ticker;
    try {
      setLoadingSections(prev => ({ ...prev, cashFlow: true }));
      
      if (!ticker || !period) {
        console.warn('No ticker or period provided for cash flow fetch');
        setCashFlowData(null);
        return;
      }
      
      // Use the enhanced financial data service
      const response = await enhancedFinancialDataService.getCashFlow(ticker, period);

      if (requestedTicker !== latestTickerRef.current) {
        return;
      }
      
      const rawData = Array.isArray(response) ? response[0] : response;
      if (rawData && rawData.sankeyData && rawData.sankeyData.nodes && rawData.sankeyData.links) {
        setCashFlowData(rawData.sankeyData);
        return;
      }

      if (rawData && rawData.nodes && rawData.links) {
        setCashFlowData(rawData);
        return;
      }

      if (rawData) {
        setCashFlowData(buildCashFlowSankey(rawData, sector));
      } else {
        setCashFlowData(null);
      }
    } catch (err) {
      console.error(`Error fetching cash flow for period ${period}:`, err);
      setCashFlowData(null);
    } finally {
      setLoadingSections(prev => ({ ...prev, cashFlow: false }));
    }
  }, [ticker, sector]);

  const fetchTranscriptForPeriod = useCallback(async (period) => {
    const requestedTicker = ticker;

    try {
      setLoadingSections((prev) => ({ ...prev, transcript: true }));

      if (!ticker) {
        setTranscriptData(null);
        setTranscriptError('No ticker selected.');
        return;
      }

      const response = period
        ? await axios.get(`${apiBaseUrl}/api/earnings-transcript/${ticker}/${period}`)
        : await axios.get(`${apiBaseUrl}/api/earnings-transcript/${ticker}`);

      if (requestedTicker !== latestTickerRef.current) {
        return;
      }

      const transcriptPayload = response?.data?.transcript || null;
      const unavailableReason = String(response?.data?.unavailableReason || '').trim();

      if (transcriptPayload) {
        setTranscriptData(transcriptPayload);
        setTranscriptError('');
        setTranscriptNotice('');
        return;
      }

      if (unavailableReason === 'transcript_provider_access_denied') {
        setTranscriptError('Transcript provider access is denied for this environment. Please update provider plan/permissions.');
      } else if (unavailableReason === 'transcript_provider_not_configured') {
        setTranscriptError('Transcript provider key is not configured on the server.');
      } else if (unavailableReason === 'transcript_rate_limited') {
        setTranscriptError('Transcript provider rate limit reached. Please retry shortly.');
      } else {
        setTranscriptError('Transcript not available for this period yet.');
      }

      if (period) {
        try {
          const latestResponse = await axios.get(`${apiBaseUrl}/api/earnings-transcript/${ticker}`);
          const latestTranscript = latestResponse?.data?.transcript || null;
          if (latestTranscript?.content) {
            setTranscriptData(latestTranscript);
            setTranscriptNotice('Selected period transcript is unavailable; showing latest available transcript.');
            setTranscriptError('');
          }
        } catch (fallbackErr) {
          console.error('Error fetching transcript fallback:', fallbackErr.response?.data || fallbackErr.message);
        }
      }
    } catch (err) {
      console.error(`Error fetching transcript for ${period || 'latest'}:`, err.response?.data || err.message);
      setTranscriptData(null);
      setTranscriptError('Unable to load transcript right now.');
    } finally {
      setLoadingSections((prev) => ({ ...prev, transcript: false }));
    }
  }, [ticker, apiBaseUrl]);

  // Handle period selection - defined after fetch functions to avoid circular dependency
  const handlePeriodSelection = useCallback(async (period) => {
    if (!period) {
      return;
    }
    
    // Always update and fetch - let the service handle caching
    setSelectedPeriod(period);
    
    try {
      // Fetch all data for this period
      setLoadingSections({
        income: true,
        cashFlow: true,
        balance: true,
        transcript: true,
      });
      
      // Use Promise.all for parallel requests
      await Promise.all([
        fetchIncomeStatementData(period),
        fetchCashFlowData(period),
        fetchBalanceSheetForPeriod(period),
        fetchTranscriptForPeriod(period)
      ]);
    } catch (err) {
      console.error('Error fetching period-specific data:', err);
      setError('Failed to load financial data for selected period');
    } finally {
      setLoadingSections({
        income: false,
        cashFlow: false,
        balance: false,
        transcript: false,
      });
    }
  }, [fetchIncomeStatementData, fetchCashFlowData, fetchBalanceSheetForPeriod, fetchTranscriptForPeriod]);

  useEffect(() => {
    if (activeView === 'transcript' && selectedPeriod && !transcriptData && !loadingSections.transcript) {
      fetchTranscriptForPeriod(selectedPeriod);
    }
  }, [activeView, selectedPeriod, transcriptData, loadingSections.transcript, fetchTranscriptForPeriod]);

  // Fetch revenue data
  const fetchRevenueData = useCallback(async () => {
    const requestedTicker = ticker;
    try {
      setLoading(true);
      
      if (!ticker) {
        console.warn('No ticker provided for revenue data fetch');
        setRevenueData([]);
        setLoading(false);
        return;
      }
      
      // Use the enhanced financial data service
      let response = await enhancedFinancialDataService.getFinancialData(ticker, false, financialPeriodType);
      if (!Array.isArray(response) || response.length === 0) {
        response = await enhancedFinancialDataService.getFinancialData(ticker, true, financialPeriodType);
      }

      if (requestedTicker !== latestTickerRef.current) {
        return;
      }

      // Detect sector from company overview for sector-specific Sankey layouts
      try {
        const overviewResponse = await axios.get(`${apiBaseUrl}/api/company-overview/${ticker}`);
        if (requestedTicker !== latestTickerRef.current) {
          return;
        }
        const overview = overviewResponse?.data || {};
        setSector(detectSector(overview.companyName || ticker, overview));
      } catch (overviewErr) {
        console.warn(`Could not fetch company overview for ${ticker}, defaulting to generic sector.`);
        setSector('generic');
      }
      
      // Always use USD for financial statements tab
      setCurrency('USD');
      
      if (!response || response.length === 0) {
        console.warn('No revenue data found');
        setRevenueData([]);
        setLoading(false);
        return;
      }

      const filteredAndSortedData = normalizeRevenueOperatingSeries(response, 10)
        .map((row) => ({
          ...row,
          Revenue: getRevenueValue(row),
          'Operating Income': getOperatingIncomeValue(row),
          'Net Income': getNetIncomeValue(row),
        }));

      const chartRows = buildChartRows(filteredAndSortedData);

      if (requestedTicker !== latestTickerRef.current) {
        return;
      }
      
      setRevenueData(chartRows);
      
      if (chartRows.length > 0) {
        const latestPeriod = chartRows[chartRows.length - 1].period;
        setSelectedPeriod(latestPeriod);
        
        // Fetch all data for this period
        await Promise.all([
          fetchIncomeStatementData(latestPeriod),
          fetchCashFlowData(latestPeriod),
          fetchBalanceSheetForPeriod(latestPeriod)
        ]);
      }
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      setError('Failed to fetch revenue data');
    } finally {
      setLoading(false);
    }
  }, [ticker, apiBaseUrl, fetchIncomeStatementData, fetchCashFlowData, fetchBalanceSheetForPeriod, financialPeriodType]);

  // Main data fetching function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await fetchRevenueData();
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch financial data');
    } finally {
      setLoading(false);
    }
  }, [fetchRevenueData]);

  useEffect(() => {
    if (ticker) {
      fetchData();
    }
  }, [ticker, fetchData]);

  // Handle clicking on a bar in the chart
  const handleBarClick = (data) => {
    if (data && data.payload && data.payload.period) {
      handlePeriodSelection(data.payload.period);
    }
  };

  // Add refresh handlers for each section
  const refreshIncomeStatement = useCallback(() => {
    if (!selectedPeriod) return;
    fetchIncomeStatementData(selectedPeriod);
  }, [selectedPeriod, fetchIncomeStatementData]);

  const refreshCashFlow = useCallback(() => {
    if (!selectedPeriod) return;
    fetchCashFlowData(selectedPeriod);
  }, [selectedPeriod, fetchCashFlowData]);

  const currentBalanceRow = balanceSheetData?.[0] || null;
  const totalAssetsValue = parseFloat(currentBalanceRow?.assets) || 0;
  const liabilitiesValue = parseFloat(currentBalanceRow?.liabilities) || 0;
  const rawCashValue = getCashFromBalanceRow(currentBalanceRow);
  const cashValue = Math.min(Math.max(rawCashValue, 0), Math.max(totalAssetsValue, 0));
  const otherAssetsValue = Math.max(totalAssetsValue - cashValue, 0);
  const balancePieData = cashValue > 0
    ? [
        { name: 'Cash', value: cashValue },
        { name: 'Other Assets', value: otherAssetsValue },
        { name: 'Liabilities', value: liabilitiesValue },
      ]
    : [
        { name: 'Assets', value: totalAssetsValue },
        { name: 'Liabilities', value: liabilitiesValue },
      ];
  const balancePieColorByName = {
    Cash: '#34d399',
    'Other Assets': '#10b981',
    Assets: '#10b981',
    Liabilities: '#ef4444',
  };

  const formatCurrencyWithSymbol = (value) => `${getCurrencySymbol()}${formatCurrency(value)}`;

  const renderBalancePieLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    value,
  }) => {
    if (!Number.isFinite(value) || value <= 0) return null;

    const RADIAN = Math.PI / 180;
    const labelRadius = innerRadius + ((outerRadius - innerRadius) * 0.62);
    const x = cx + labelRadius * Math.cos(-midAngle * RADIAN);
    const y = cy + labelRadius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#ffffff"
        stroke="rgba(10, 15, 28, 0.95)"
        strokeWidth={3}
        paintOrder="stroke"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={isMobile ? 13 : 15}
        fontWeight={800}
      >
        {formatCurrencyWithSymbol(value)}
      </text>
    );
  };

  const refreshBalanceSheet = useCallback(() => {
    if (!selectedPeriod) return;
    fetchBalanceSheetForPeriod(selectedPeriod);
  }, [selectedPeriod, fetchBalanceSheetForPeriod]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      {/* View switcher: Statements vs SEC Filings */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          color="primary"
          value={activeView}
          onChange={(_e, val) => { if (val) setActiveView(val); }}
          aria-label="Financial statements view"
        >
          <ToggleButton value="statements" aria-label="Statements">
            <AssessmentIcon fontSize="small" sx={{ mr: 0.5 }} /> Statements
          </ToggleButton>
          <ToggleButton value="filings" aria-label="SEC Filings">
            <DescriptionIcon fontSize="small" sx={{ mr: 0.5 }} /> SEC Filings
          </ToggleButton>
          <ToggleButton value="transcript" aria-label="Earnings Transcript">
            Earnings Transcript
          </ToggleButton>
        </ToggleButtonGroup>
        {ticker && (
          <Typography variant="body2" color="text.secondary">
            {ticker}
          </Typography>
        )}
      </Box>

      {activeView === 'filings' ? (
        <SecFilings ticker={ticker} />
      ) : activeView === 'transcript' ? (
        <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Box>
              <Typography variant="h6">Earnings Call Transcript</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPeriod
                  ? `Selected period: ${new Date(selectedPeriod).toLocaleDateString()}`
                  : 'Select a period from the charts below to load a transcript.'}
              </Typography>
            </Box>
            {selectedPeriod && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => fetchTranscriptForPeriod(selectedPeriod)}
                disabled={loadingSections.transcript}
              >
                Refresh
              </Button>
            )}
          </Box>

          {transcriptNotice && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {transcriptNotice}
            </Alert>
          )}

          {loadingSections.transcript ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading transcript...</Typography>
            </Box>
          ) : transcriptError ? (
            <Alert severity="info">{transcriptError}</Alert>
          ) : transcriptData?.content ? (
            <Box>
              {transcriptData.title && (
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  {transcriptData.title}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  maxHeight: '62vh',
                  overflowY: 'auto',
                }}
              >
                {transcriptData.content}
              </Typography>
            </Box>
          ) : (
            <Alert severity="info">
              {selectedPeriod ? 'Transcript not available for this period.' : 'Select a period to load a transcript.'}
            </Alert>
          )}
        </Paper>
      ) : (
        <>
      {/* Period selector */}
      <Box mb={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
          <Typography variant="subtitle1">Select a financial period:</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={financialPeriodType}
            onChange={(_event, nextType) => {
              if (nextType) setFinancialPeriodType(nextType);
            }}
          >
            <ToggleButton value="quarterly">Quarterly</ToggleButton>
            <ToggleButton value="annual">Annual</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <ButtonGroup size="small" variant="outlined">
            {revenueData.map((data, index) => (
              <Button 
                key={index} 
                onClick={() => handlePeriodSelection(data.period)}
                variant={selectedPeriod === data.period ? 'contained' : 'outlined'}
              >
                {new Date(data.period).toLocaleDateString()}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      </Box>
      
      {/* Data visualizations - Scrollable horizontally */}
      <Box 
        sx={{ 
          mb: 4, 
          height: { xs: '56vh', sm: '50vh' },
          overflowX: 'auto',  // Allows horizontal scrolling
          whiteSpace: 'nowrap', // Prevents wrapping
          pb: 2, // Add some padding at the bottom for scrollbar
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        <Box 
          sx={{ 
            display: 'inline-flex', // Use inline-flex for horizontal layout
            gap: { xs: 1.5, sm: 3 }, // Space between each visualization
            minWidth: '100%', // At minimum, take up the full container width
            height: '100%',
          }}
        >
          {/* Balance Sheet */}
          <Paper
            elevation={3} 
            sx={{ 
              p: { xs: 1.25, sm: 2 }, 
              width: { xs: '82vw', sm: '500px' },
              height: '100%',
              display: 'inline-block',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ mb: 0 }}>
                Balance Sheet
              </Typography>
              <IconButton
                size="small"
                onClick={() => setBalanceSheetInfoDialogOpen(true)}
                sx={{ p: 0, minWidth: '24px', width: '24px', height: '24px' }}
                title="View component descriptions"
              >
                <InfoOutlinedIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            </Box>
            <Box height="90%" position="relative">
              {loadingSections.balance && (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%" position="absolute" width="100%" bgcolor="rgba(255,255,255,0.7)" zIndex={1}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>Loading...</Typography>
                </Box>
              )}
              <ResponsiveContainer width="100%" height="100%">
                {balanceSheetData.length > 0 && 
                 balanceSheetData[0].assets !== undefined &&
                 balanceSheetData[0].liabilities !== undefined ? (
                  <PieChart>
                    <Pie
                      data={balancePieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={isMobile ? 96 : 120}
                      label={renderBalancePieLabel}
                      labelLine={false}
                      dataKey="value"
                    >
                      {balancePieData.map((entry, index) => (
                        <Cell
                          key={`balance-pie-cell-${index}`}
                          fill={balancePieColorByName[entry.name] || '#9ca3af'}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrencyWithSymbol(Number(value))} />
                    <Legend />
                  </PieChart>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <Typography>{loadingSections.balance ? '' : 'No data available'}</Typography>
                  </Box>
                )}
              </ResponsiveContainer>
              {/* Add refresh button for balance sheet */}
              <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 10, m: 1 }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={refreshBalanceSheet} 
                  disabled={loadingSections.balance}
                  sx={{ minWidth: '30px', width: '30px', height: '30px', p: 0 }}
                >
                  <span role="img" aria-label="refresh">🔄</span>
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Balance Sheet Component Descriptions Dialog */}
          <Dialog
            open={balanceSheetInfoDialogOpen}
            onClose={() => setBalanceSheetInfoDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                maxHeight: '80vh',
                backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'
              }
            }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Balance Sheet Components</Typography>
              <IconButton
                onClick={() => setBalanceSheetInfoDialogOpen(false)}
                size="small"
                sx={{ ml: 1 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ overflowY: 'auto', py: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {balanceLabelHeaders.map((label) => {
                  const componentInfo = getComponentDescription(label, 'balance');
                  const likelySubComponents = componentInfo?.subComponents || [
                    'See company filings for detailed line items',
                    'Typical composition varies by industry'
                  ];

                  return (
                  <Box key={label} sx={{ pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { borderBottom: 'none' } }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 0.5 }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mb: 0.5, color: theme.palette.text.secondary }}>
                      Typical Sub-components:
                    </Typography>
                    <List sx={{ py: 0, ml: 1 }}>
                      {likelySubComponents.map((subComponent, idx) => (
                        <ListItem key={idx} sx={{ py: 0.25, px: 0, pl: 1 }}>
                          <ListItemText
                            primary={subComponent}
                            primaryTypographyProps={{ variant: 'caption', sx: { color: theme.palette.text.primary } }}
                            sx={{ my: 0 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  );
                })}
              </Box>
            </DialogContent>
          </Dialog>

          {/* Income Statement - Increased width */}
          <Paper
            elevation={3} 
            sx={{ 
              p: { xs: 1.25, sm: 2 }, 
              width: { xs: '88vw', sm: '1000px' },
              height: '100%',
              display: 'inline-block',
            }}
          >
            <IncomeBridgeWaterfall
              key={`income-waterfall-${selectedPeriod}`}
              data={incomeStatementData}
              title={`Income Statement ${selectedPeriod ? `- ${new Date(selectedPeriod).toLocaleDateString()}` : ''}`}
              loading={loadingSections.income}
              currency={currency}
            />
          </Paper>

          {/* Cash Flow - Increased width */}
          <Paper
            elevation={3} 
            sx={{ 
              p: { xs: 1.25, sm: 2 }, 
              width: { xs: '110vw', sm: '1500px' },
              height: '100%',
              display: 'inline-block',
            }}
          >
            <ReactSankeyDiagram
              key={`cashflow-${selectedPeriod}`}
              data={cashFlowData}
              title={`Cash Flow ${selectedPeriod ? `- ${new Date(selectedPeriod).toLocaleDateString()}` : ''}`}
              loading={loadingSections.cashFlow}
              onRefresh={refreshCashFlow}
              currency={currency}
              statementType="cashflow"
            />
          </Paper>
        </Box>
      </Box>

      {/* Revenue chart */}
      <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <Typography variant="h6" sx={{ mb: 0 }}>
            Revenue, Operating Income, and Net Income/Loss {currency !== 'USD' && `(${currency})`}
          </Typography>
          {revenueData.some((row) => row.isPreliminary) ? (
            <Chip
              size="small"
              label={getPreliminary8kLabel(
                revenueData.find((row) => row.isPreliminary)?.preliminaryLabel
              )}
              sx={{
                bgcolor: 'rgba(6, 182, 212, 0.18)',
                color: '#06b6d4',
                border: '1px solid rgba(6, 182, 212, 0.6)',
                fontWeight: 800,
              }}
            />
          ) : null}
        </Box>
        <Box sx={{ height: { xs: 320, sm: 400 } }}>
          <ResponsiveContainer>
            {(() => {
              const ipoAccent = '#f59e0b';
              const prelimAccent = '#06b6d4';
              const ipoRow = revenueData.find((row) => row.isIpoPeriod) || null;
              const ipoPeriod = ipoRow?.period || null;
              const ipoLabel = ipoRow?.ipoLabel || getIpoPeriodLabel(ipoRow?.sourceForm);
              const prelimRow = revenueData.find((row) => row.isPreliminary) || null;
              const prelimPeriod = prelimRow?.period || null;
              const prelimLabel = getPreliminary8kLabel(prelimRow?.preliminaryLabel);
              const formatPeriodTick = (value, row) => {
                const base = new Date(value).toLocaleDateString('en-US', {
                  year: '2-digit',
                  month: 'short',
                });
                if (row?.isIpoPeriod) {
                  return `${base} · ${row.ipoLabel || getIpoPeriodLabel(row.sourceForm)}`;
                }
                if (row?.isPreliminary) {
                  return `${base} · ${row.preliminaryLabel || getPreliminary8kLabel()}`;
                }
                return base;
              };
              return (
            <LineChart
              data={revenueData}
              margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: isMobile ? 56 : 70 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="period" 
                tick={(tickProps) => {
                  const { x, y, payload } = tickProps;
                  const row = revenueData.find((item) => String(item.period) === String(payload?.value));
                  const isIpo = Boolean(row?.isIpoPeriod);
                  const isPrelim = Boolean(row?.isPreliminary);
                  const label = formatPeriodTick(payload?.value, row);
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        dy={12}
                        textAnchor="end"
                        transform={`rotate(${isMobile ? -35 : -45})`}
                        fill={isIpo ? ipoAccent : (isPrelim ? prelimAccent : theme.palette.text.secondary)}
                        fontSize={(isIpo || isPrelim) ? (isMobile ? 10 : 12) : (isMobile ? 10 : 12)}
                        fontWeight={(isIpo || isPrelim) ? 800 : 500}
                      >
                        {label}
                      </text>
                    </g>
                  );
                }}
                angle={isMobile ? -35 : -45}
                textAnchor="end"
                height={isMobile ? 56 : 70}
              /> 
              <YAxis 
                tickFormatter={(value) => {
                  const symbol = getCurrencySymbol();
                  // Format based on value magnitude
                  if (Math.abs(value) >= 1e9) return `${symbol}${(value / 1e9).toFixed(1)}B`;
                  if (Math.abs(value) >= 1e6) return `${symbol}${(value / 1e6).toFixed(1)}M`;
                  if (Math.abs(value) >= 1e3) return `${symbol}${(value / 1e3).toFixed(1)}K`;
                  return `${symbol}${value.toFixed(1)}`;
                }}
                label={isMobile ? undefined : { value: `Value (${getCurrencySymbol()})`, angle: -90, position: 'insideLeft' }}
                tickCount={7}
                width={isMobile ? 56 : 72}
                domain={[
                  Math.min(
                    getDataRange(revenueData, 'Revenue').min, 
                    getDataRange(revenueData, 'Operating Income').min,
                    getDataRange(revenueData, 'Net Income').min
                  ), 
                  Math.max(
                    getDataRange(revenueData, 'Revenue').max, 
                    getDataRange(revenueData, 'Operating Income').max,
                    getDataRange(revenueData, 'Net Income').max
                  )
                ]}
              />
              <Tooltip 
                formatter={(value, name) => {
                  const symbol = getCurrencySymbol();
                  // Format based on value magnitude
                  if (Math.abs(value) >= 1e9) return [`${symbol}${(value / 1e9).toFixed(2)}B`, name];
                  if (Math.abs(value) >= 1e6) return [`${symbol}${(value / 1e6).toFixed(2)}M`, name];
                  if (Math.abs(value) >= 1e3) return [`${symbol}${(value / 1e3).toFixed(2)}K`, name];
                  return [`${symbol}${value.toFixed(2)}`, name];
                }}
                labelFormatter={(label, payload) => {
                  const row = payload?.[0]?.payload;
                  const base = `Period: ${new Date(label).toLocaleDateString()}`;
                  if (row?.isIpoPeriod) {
                    return `${base} · ${row.ipoLabel || getIpoPeriodLabel(row.sourceForm)}`;
                  }
                  if (row?.isPreliminary) {
                    return `${base} · ${row.preliminaryLabel || getPreliminary8kLabel()} (not yet in 10-Q/10-K)`;
                  }
                  return base;
                }}
                contentStyle={{
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 24, 39, 0.96)' : '#ffffff',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
                labelStyle={{
                  color: theme.palette.mode === 'dark' ? '#f3f4f6' : '#111827',
                  fontWeight: 600,
                }}
                itemStyle={{
                  color: theme.palette.mode === 'dark' ? '#e5e7eb' : '#1f2937',
                }}
              />
              <Legend wrapperStyle={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }} />
              {ipoPeriod ? (
                <ReferenceLine
                  x={ipoPeriod}
                  stroke={ipoAccent}
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{
                    value: ipoLabel,
                    position: 'insideTopLeft',
                    fill: ipoAccent,
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                />
              ) : null}
              {prelimPeriod ? (
                <ReferenceLine
                  x={prelimPeriod}
                  stroke={prelimAccent}
                  strokeDasharray="3 3"
                  strokeWidth={1.75}
                  label={{
                    value: prelimLabel,
                    position: 'insideTopRight',
                    fill: prelimAccent,
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                />
              ) : null}
              <Line
                type="monotone"
                dataKey="Revenue"
                name="Revenue"
                stroke="#8884d8"
                strokeWidth={3}
                dot={{ r: 3, fill: '#8884d8' }}
                activeDot={{ r: 6 }}
                onClick={handleBarClick}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="Operating Income"
                name="Operating Income"
                stroke="#82ca9d"
                strokeWidth={3}
                dot={{ r: 3, fill: '#82ca9d' }}
                activeDot={{ r: 6 }}
                onClick={handleBarClick}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="Net Income"
                name="Net Income/Loss"
                stroke="#f59e0b"
                strokeWidth={3}
                dot={{ r: 3, fill: '#f59e0b' }}
                activeDot={{ r: 6 }}
                onClick={handleBarClick}
                connectNulls={false}
              />
            </LineChart>
              );
            })()}
          </ResponsiveContainer>
        </Box>
      </Paper>
        </>
      )}
    </Box>
  );
};

export default FinancialStatements;